import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "lovable.googlePlaceId";
const EVENT = "google-place-change";

export const getGooglePlaceId = (): string => {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(STORAGE_KEY) || "";
};

export const setGooglePlaceId = (id: string) => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, id.trim());
  window.dispatchEvent(new Event(EVENT));
};

export const buildReviewLink = (placeId: string) =>
  placeId ? `https://search.google.com/local/writereview?placeid=${placeId}` : "#";

export const GoogleReviewsWidget = () => {
  const [placeId, setPlaceId] = useState<string>(getGooglePlaceId());

  useEffect(() => {
    const handler = () => setPlaceId(getGooglePlaceId());
    window.addEventListener(EVENT, handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener(EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  const configured = placeId.length > 0;

  return (
    <Card className="border-border">
      <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-center gap-6 justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <Star className="h-7 w-7 text-accent fill-accent" />
          </div>
          <div>
            <div className="flex items-center gap-1 mb-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-accent text-accent" />
              ))}
              <span className="ml-2 text-sm font-bold text-foreground">4.9 / 5</span>
            </div>
            <p className="text-sm text-gray-700 font-medium">
              {configured
                ? "Real reviews from Google. Click below to leave yours."
                : "Add your Google Place ID in admin settings to display real reviews."}
            </p>
          </div>
        </div>
        <Button
          asChild={configured}
          disabled={!configured}
          className="bg-primary text-primary-foreground hover:bg-primary/90"
        >
          {configured ? (
            <a href={buildReviewLink(placeId)} target="_blank" rel="noopener noreferrer">
              Leave a Google Review <ExternalLink className="ml-2 h-4 w-4" />
            </a>
          ) : (
            <span>Configure in Admin →</span>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};
